import { useEffect } from "react";

// Smooth discrete wheel input without replacing the page's native scroll area.
// Touch, zoom, form controls and nested scrollers retain browser behavior.
export function useSmoothScroll() {
  useEffect(() => {
    if (!window.matchMedia) return;
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    let frame = 0;
    let target = window.scrollY;
    let previousTime = 0;

    const cancel = () => {
      window.cancelAnimationFrame(frame);
      frame = 0;
    };

    const tick = (time: number) => {
      target = Math.min(
        target,
        Math.max(0, document.documentElement.scrollHeight - window.innerHeight),
      );
      const distance = target - window.scrollY;
      const progress = 1 - Math.exp(-(time - previousTime) / 85);
      previousTime = time;
      if (Math.abs(distance) <= 1) {
        window.scrollTo({ top: target, behavior: "instant" });
        frame = 0;
        return;
      }
      const movement = distance * progress;
      window.scrollTo({
        top:
          window.scrollY +
          Math.sign(distance) * Math.max(1, Math.abs(movement)),
        behavior: "instant",
      });
      frame = window.requestAnimationFrame(tick);
    };

    const onWheel = (event: WheelEvent) => {
      if (
        reducedMotion.matches ||
        event.defaultPrevented ||
        !event.cancelable ||
        event.ctrlKey ||
        event.metaKey ||
        Math.abs(event.deltaX) > Math.abs(event.deltaY)
      )
        return;
      const element = event.target instanceof Element ? event.target : null;
      if (
        element?.closest(
          "input, textarea, select, [contenteditable], [data-native-scroll]",
        )
      )
        return;
      for (
        let parent = element;
        parent &&
        parent !== document.body &&
        parent !== document.documentElement;
        parent = parent.parentElement
      ) {
        if (
          parent.scrollHeight > parent.clientHeight &&
          /^(auto|scroll)$/.test(getComputedStyle(parent).overflowY)
        )
          return;
      }
      const unit =
        event.deltaMode === WheelEvent.DOM_DELTA_LINE
          ? 16
          : event.deltaMode === WheelEvent.DOM_DELTA_PAGE
            ? window.innerHeight
            : 1;
      const delta = event.deltaY * unit;
      if (!delta) return;
      if (!frame) target = window.scrollY;
      const maximum = Math.max(
        0,
        document.documentElement.scrollHeight - window.innerHeight,
      );
      const next = Math.max(0, Math.min(maximum, target + delta));
      if (next === window.scrollY && !frame) return;
      event.preventDefault();
      target = next;
      if (!frame) {
        previousTime = performance.now();
        frame = window.requestAnimationFrame(tick);
      }
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (
        [
          "ArrowUp",
          "ArrowDown",
          "PageUp",
          "PageDown",
          "Home",
          "End",
          " ",
        ].includes(event.key)
      )
        cancel();
    };

    window.addEventListener("wheel", onWheel, { passive: false });
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("pointerdown", cancel, { passive: true });
    window.addEventListener("touchstart", cancel, { passive: true });
    window.addEventListener("resize", cancel);
    window.addEventListener("hashchange", cancel);
    reducedMotion.addEventListener("change", cancel);
    return () => {
      cancel();
      window.removeEventListener("wheel", onWheel);
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("pointerdown", cancel);
      window.removeEventListener("touchstart", cancel);
      window.removeEventListener("resize", cancel);
      window.removeEventListener("hashchange", cancel);
      reducedMotion.removeEventListener("change", cancel);
    };
  }, []);
}
